import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { cn } from '../../../utils/cn';

const ProfileHeader = ({ user, onUpdate }) => {
  const [isAvatarUploading, setIsAvatarUploading] = useState(false);
  const [avatarPreview, setAvatarPreview] = useState(null);

  const handleAvatarUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      alert('Bitte wählen Sie ein gültiges Bildformat (JPEG, PNG, GIF, WebP)');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file?.size > maxSize) {
      alert('Die Datei ist zu groß. Maximale Größe: 5MB');
      return;
    }

    setIsAvatarUploading(true);

    // Create preview
    const reader = new FileReader();
    reader.onload = (e) => {
      const newAvatarUrl = e?.target?.result;
      setAvatarPreview(newAvatarUrl);

      // Simulate upload delay
      setTimeout(() => {
        onUpdate?.({ avatar: newAvatarUrl });
        setIsAvatarUploading(false);
        setAvatarPreview(null);
      }, 1500);
    };
    reader?.readAsDataURL(file);
  };

  const triggerFileInput = () => {
    document?.getElementById('avatar-upload')?.click();
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInitials = () => {
    const firstName = user?.firstName || '';
    const lastName = user?.lastName || '';
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`?.toUpperCase();
  };

  const currentAvatar = avatarPreview || user?.avatar;

  return (
    <div className="bg-card rounded-xl border border-border p-8 mb-8">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
        {/* Left Section - Avatar and Basic Info */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          {/* Avatar */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted flex items-center justify-center">
              {currentAvatar ? (
                <img 
                  src={currentAvatar} 
                  alt="Profil"
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : (
                <span className="text-2xl font-semibold text-muted-foreground">
                  {getInitials()}
                </span>
              )}
              {!currentAvatar && (
                <span className="text-2xl font-semibold text-muted-foreground" style={{display: currentAvatar ? 'none' : 'flex'}}>
                  {getInitials()}
                </span>
              )}
            </div>

            {/* Upload Button Overlay */}
            <button
              onClick={triggerFileInput}
              disabled={isAvatarUploading}
              className={cn(
                "absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity",
                isAvatarUploading && "opacity-100"
              )}
            >
              {isAvatarUploading ? (
                <Icon name="Loader2" size={20} className="text-white animate-spin" />
              ) : (
                <Icon name="Camera" size={20} className="text-white" />
              )}
            </button>

            <input
              id="avatar-upload"
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>

          {/* Basic Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl font-bold text-foreground truncate">
                {user?.firstName} {user?.lastName}
              </h1>
              {user?.emailVerified && (
                <div className="flex items-center gap-1 px-2 py-1 bg-success/10 text-success rounded-full">
                  <Icon name="CheckCircle" size={14} />
                  <span className="text-xs font-medium">Verifiziert</span>
                </div>
              )}
            </div>
            
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-muted-foreground">
              <div className="flex items-center gap-1">
                <Icon name="Mail" size={14} />
                <span className="text-sm truncate">{user?.email}</span>
              </div>
              
              {user?.jobTitle && (
                <div className="flex items-center gap-1">
                  <Icon name="Briefcase" size={14} />
                  <span className="text-sm truncate">{user?.jobTitle}</span>
                </div>
              )}
              
              {user?.company && (
                <div className="flex items-center gap-1">
                  <Icon name="Building" size={14} />
                  <span className="text-sm truncate">{user?.company}</span>
                </div>
              )}
            </div>

            {user?.bio && (
              <p className="mt-3 text-sm text-muted-foreground line-clamp-2">
                {user?.bio}
              </p>
            )}
          </div>
        </div>

        {/* Right Section - Stats and Actions */}
        <div className="flex flex-col sm:flex-row lg:flex-col gap-4">
          {/* Stats */}
          <div className="flex sm:flex-col gap-4 sm:gap-2">
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{user?.agentsCount || 0}</div>
              <div className="text-xs text-muted-foreground">AI-Agents</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-foreground">{user?.apiKeysCount || 0}</div>
              <div className="text-xs text-muted-foreground">API-Keys</div>
            </div>
          </div>

          {/* Member Since */}
          <div className="text-center lg:text-right">
            <div className="text-xs text-muted-foreground mb-1">Mitglied seit</div>
            <div className="text-sm font-medium text-foreground">
              {formatDate(user?.joinedDate)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileHeader;